import http from '@/utils/httpRequestHelper'

// ── AMap POI search (called directly from browser) ──
const AMAP_KEY = 'b5d08167a88e7d83176639b8eba27be0'

export const searchAmapPoi = async (keywords, city) => {
  const params = new URLSearchParams({
    key: AMAP_KEY,
    keywords: keywords || '',
    offset: '10',
    page: '1',
    output: 'JSON'
  })
  if (city) params.set('city', city)

  const res = await fetch(`https://restapi.amap.com/v3/place/text?${params}`)
  if (!res.ok) throw new Error('AMap request failed: ' + res.status)

  const data = await res.json()
  if (data.status !== '1') throw new Error(data.info || 'AMap error')

  return (data.pois || []).map(poi => {
    const [lng, lat] = (poi.location || '').split(',').map(Number)
    return {
      name: poi.name || '',
      address: poi.address || '',
      province: poi.pname || '',
      city: poi.cityname || '',
      district: poi.adname || '',
      lat: lat || 0,
      lng: lng || 0,
      tel: poi.tel || '',
      type: poi.type || ''
    }
  })
}

/**
 * Address → coordinates (AMap geocoding). Used by the batch store import to
 * derive lng/lat for rows that don't carry them. Returns null when the address
 * can't be resolved, so the caller can flag the row instead of failing.
 */
export const geocodeAmapAddress = async (address, city) => {
  const query = (address || '').trim()
  if (!query) return null

  const params = new URLSearchParams({ key: AMAP_KEY, address: query, output: 'JSON' })
  if (city) params.set('city', city)

  const res = await fetch(`https://restapi.amap.com/v3/geocode/geo?${params}`)
  if (!res.ok) throw new Error('AMap request failed: ' + res.status)

  const data = await res.json()
  // status 0 with INVALID_USER_KEY / OVER_QUOTA etc. is a hard error worth
  // surfacing; an empty geocodes array just means "not found".
  if (data.status !== '1') throw new Error(data.info || 'AMap error')

  const hit = (data.geocodes || [])[0]
  if (!hit || !hit.location) return null
  const [lng, lat] = String(hit.location).split(',').map(Number)
  if (!lng || !lat) return null

  return {
    lng,
    lat,
    formattedAddress: hit.formatted_address || '',
    // AMap returns [] instead of a string for empty region fields.
    province: typeof hit.province === 'string' ? hit.province : '',
    city: typeof hit.city === 'string' ? hit.city : '',
    district: typeof hit.district === 'string' ? hit.district : '',
    level: hit.level || '',
  }
}

// ── Batch CREATE new stores (CSV/xlsx upload → preview → geocode → apply) ──
// The update-batch tool above only updates stores it can match by phone; rows
// for stores that don't exist yet are reported as "unmatched" and dropped.
// These create the missing ones. Coordinates may be omitted from the file —
// the client geocodes them from the address before applying.
export const previewStoreCreate = (file) => {
  const fd = new FormData()
  fd.append('file', file)
  return http.postRequest('/ai/brand/stores/create-batch', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
}
export const applyStoreCreates = (rows) =>
  http.postRequest('/ai/brand/stores/create-batch/apply', { rows }, { timeout: 120000 })

// ── Dashboard (brand overview stats) - read only ──
export const getDashboard = (params) => http.getRequest('/ai/brand/dashboard', params)
// days: 7 / 30 / 365; omit for all time
export const getDashboardTopColors = (params) => http.getRequest('/ai/brand/dashboard/top-colors', params)

// ── Leads (free_token_claim) - read only ──
export const listClaims = (params) => http.getRequest('/ai/brand/claims', params)
export const exportClaims = (params) => http.getRequest('/ai/brand/claims/export', params)

// ── Stores (car_wrap_store) - CRUD ──
export const listStores = (params) => http.getRequest('/ai/brand/stores', params)
export const exportStores = (params) => http.getRequest('/ai/brand/stores/export', params)
export const getStore = (id) => http.getRequest('/ai/brand/stores/' + id)
export const createStore = (data) => http.postRequest('/ai/brand/stores', data)
export const updateStore = (id, data) => http.putRequest('/ai/brand/stores/' + id, data)
export const deleteStore = (id) => http.deleteRequest('/ai/brand/stores/' + id)

// ── Batch update by phone (CSV upload → preview → apply) ──
// Upload a CSV (exported from the store list, then edited); the backend
// matches rows to existing stores by phone and returns a per-row diff plus
// `availableFields` (which columns are present). Then apply the chosen
// fields for the reviewed rows.
export const previewStoreUpdate = (file) => {
    const fd = new FormData()
    fd.append('file', file)
    return http.postRequest('/ai/brand/stores/update-batch', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
    })
}
export const applyStoreUpdates = (rows, fields) =>
    http.postRequest('/ai/brand/stores/update-batch/apply', { rows, fields }, { timeout: 60000 })
